<template>
	<div>
		<v-card style="box-shadow: none !important" :disabled="disabled">
			<v-card-text style="padding: 0px">
				<AppointmentConfirmation
					:appointment="appointment"
					:conversation="conversation"
					:time-zone="timeZone"
					:name="customerName"
					:phone="customerPhone"
					:show-cancel-call="true"
					:show-reschedule="true"
					:delivery="delivery ?? undefined"
					:can-repair-delivery="canRepairDelivery ?? false"
					:email-invitations-allowed="emailInvitationsAllowed"
					class="session-appointment"
					@cancel-call="$emit('cancel-call')"
					@reschedule-call="openRescheduleDialog"
					@delivery-resent="$emit('delivery-resent', $event)"
					@delivery-refresh="$emit('delivery-refresh')"
					@delivery-customer-updated="$emit('delivery-customer-updated', $event)" />
			</v-card-text>
		</v-card>
		<!-- Outside the v-card on purpose: that card is `:disabled` while the free trial has ended, and a
		     dialog is not part of that surface.

		     `:date-format="dateFormat ?? undefined"` rather than the raw value: the dialog declares that
		     prop with `default: null`, which vue-tsc infers as `string`, so passing `string | null` is a
		     type error. Undefined takes the same default, so the runtime value is identical. -->
		<RescheduleCallDialog
			v-if="appointment"
			v-model="isRescheduleDialogOpen"
			:appointment="appointment"
			:contact-first-name="contactFirstName"
			:time-zone="timeZone"
			:date-format="dateFormat ?? undefined"
			:message-template="messageTemplate"
			:message-time-zone="messageTimeZone"
			:team-member-name="teamMemberName"
			:organization-name="organizationName"
			:saving="isRescheduling"
			@rescheduled="reschedule" />
	</div>
</template>

<script setup lang="ts">
	/**
	 * LSP-16658 — the scheduled-call card and everything the reschedule needs to run, scoped to ONE
	 * conversation.
	 *
	 * This exists because the reschedule state used to live on EntityDetailPage, which outlives every
	 * conversation it shows. Four separate defects came out of that single fact: a dialog that stayed
	 * open across an entity switch, a stale response that closed a DIFFERENT conversation's dialog, a
	 * stale `finally` that stripped another conversation's loading state, and — after the state was
	 * keyed by conversation id to fix those — a dialog that reopened on returning to the same call,
	 * because the key was never cleared. Every one of them was the same bug: page-lifetime state
	 * pretending to be per-conversation state.
	 *
	 * The parent mounts this with `:key="entity.id"`, so leaving the conversation DESTROYS it. The open
	 * flag, the in-flight flag and the per-conversation lock cannot outlive their conversation, cannot
	 * be seen by another one, and there is nothing to clear on a switch.
	 *
	 * Destruction is NOT the whole story, and the first draft of this comment wrongly said it was: an
	 * in-flight request is not cancelled by unmounting, and `toast` is a global singleton that will
	 * happily fire from a dead component. The emit is dropped, but the toast is not — so the unmount
	 * flag below is what actually stops a late response announcing a conversation the user has left.
	 *
	 * The page keeps what is genuinely the page's: the About-form dirty signal (the reschedule refuses
	 * to start while the form is dirty) and the merge + re-baseline of the returned conversation.
	 */
	import { onBeforeUnmount, ref } from "vue";
	import AppointmentConfirmation from "@/components/AppointmentConfirmation.vue";
	import RescheduleCallDialog from "@/components/RescheduleCallDialog.vue";
	import api from "@/api";
	import type { ConversationDelivery } from "@/api/index.d";
	import type { SmsDeliveryRepairCustomer } from "@/helpers/smsDelivery";
	import { useToast } from "vue-toastification";
	import { calculateTimeout, getMessageFromError } from "@/helpers/utilities";

	const props = withDefaults(
		defineProps<{
			conversationId: string;
			appointment: Record<string, any> | undefined;
			conversation: Record<string, any> | undefined;
			customerName: string;
			customerPhone: string;
			contactFirstName: string;
			timeZone: string;
			dateFormat: string | null;
			/**
			 * The organization's scheduled-call template, the conversation's assigned team member, and the
			 * organization name — everything the dialog needs to render the reschedule message the same way
			 * the booking message was rendered. Passed down rather than fetched here: the page already holds
			 * the `/me` payload and the conversation, and a second request for data already on screen is one
			 * more thing that can fail between opening the dialog and seeing a message in it.
			 */
			messageTemplate: string;
			/**
			 * The stored booking zone the CONTACT-facing message renders in. `timeZone` (which drives the card
			 * and the pickers) is the same zone when it is valid, and this admin's browser zone otherwise
			 * (LSP-21861). See the dialog's prop.
			 */
			messageTimeZone: string;
			teamMemberName: string;
			organizationName: string;
			disabled: boolean;
			/**
			 * True while the About form has unsaved edits. The reschedule then refuses to start: the server
			 * rewrites the conversation name, the appointment and the calendar links, and reconciling that
			 * against a half-typed form is the mechanism this feature already deleted once. Owned by the
			 * page because that is where the form lives.
			 */
			isAboutFormDirty: boolean;
			// LSP-20787: absent (undefined) on a page that has not loaded a delivery state yet.
			delivery?: ConversationDelivery | null;
			canRepairDelivery?: boolean;
			// Final review I1: the organization's "Allow Email Invitations"; off hides the card's Send via email.
			emailInvitationsAllowed?: boolean;
		}>(),
		{ emailInvitationsAllowed: true }
	);

	const emit = defineEmits<{
		(event: "cancel-call"): void;
		(event: "rescheduled", conversation: Record<string, any>): void;
		// Bugbot: conversationId is the id the send was actually made for (EntityDetailPage compares it to the
		// session it currently shows before refreshing anything for it).
		(event: "delivery-resent", conversationId: string): void;
		(event: "delivery-refresh"): void;
		(event: "delivery-customer-updated", customer: SmsDeliveryRepairCustomer): void;
	}>();

	const toast = useToast();

	// Destroying the component does NOT cancel an in-flight request, and `toast` is a global singleton
	// that does not care whether its caller is still mounted — so "the child was destroyed" stops the
	// emit but would NOT stop a success toast naming a contact the user has already navigated away
	// from. Found by this component's own destroyed-response test, which is exactly the class of stale
	// visible effect the page-level version kept getting wrong. One flag, checked before every
	// user-visible effect after an await.
	let isUnmounted = false;
	onBeforeUnmount(() => {
		isUnmounted = true;
	});

	const isRescheduleDialogOpen = ref(false);
	const isRescheduling = ref(false);
	// Still a lock rather than just the boolean above: the dialog's agree button is deliberately never
	// disabled on validity, so a double-click emits twice, and only the FIRST may reach the API.
	let isRequestInFlight = false;

	function openRescheduleDialog(): void {
		if (!props.appointment?.startAt) {
			// The card only renders with an appointment, so this is a "shouldn't happen" — but opening a
			// reschedule dialog with nothing to reschedule FROM renders an empty "Currently scheduled"
			// panel and no prefill, which reads as a broken dialog rather than as missing data.
			console.warn("ScheduledCallActions: the conversation has no appointment start time, not opening the dialog", {
				conversationId: props.conversationId
			});
			return;
		}
		if (props.isAboutFormDirty) {
			console.warn("ScheduledCallActions: the About form has unsaved edits — refusing to open the reschedule dialog", {
				conversationId: props.conversationId
			});
			const unsavedMessage = "Save or discard your changes first, then reschedule.";
			toast.info(unsavedMessage, { timeout: calculateTimeout(unsavedMessage) });
			return;
		}
		isRescheduleDialogOpen.value = true;
	}

	// LSP-22022: the counts of new-time notices the API QUEUED per channel. Absent on an older API.
	type RescheduleNotice = { sms: number; email: number };

	// LSP-22022: say what actually went out — "has been notified" was shown even when no text or email was queued.
	// Queued is not delivered: the delivery chip/banner (reloaded by the page) reports the text's real outcome.
	function announceReschedule(notice: RescheduleNotice | undefined): void {
		if (!notice || typeof notice.sms !== "number" || typeof notice.email !== "number") {
			toast.success("Call rescheduled.");
			return;
		}
		// The page passes "Your contact" when the contact has no name — a sentence opener, not a mid-sentence name.
		const name = props.contactFirstName && props.contactFirstName !== "Your contact" ? props.contactFirstName : "";
		const sending =
			notice.sms > 0 && notice.email > 0 ? "texting and emailing" : notice.sms > 0 ? "texting" : notice.email > 0 ? "emailing" : null;
		if (sending) {
			toast.success(`Call rescheduled. We're ${sending} ${name || "the contact"} the new time.`);
			return;
		}
		const message = `Call rescheduled. ${name || "The contact"} wasn't sent the new time — no text or email went out.`;
		toast.info(message, { timeout: calculateTimeout(message) });
	}

	async function reschedule(payload: { startAt: number; timeZone: string; note?: string }): Promise<void> {
		if (!payload || typeof payload.startAt !== "number" || !Number.isFinite(payload.startAt)) {
			// The dialog cannot emit this shape today, but the server's 400 for it is indistinguishable
			// from a dozen others, so name it here instead of sending it.
			console.warn("ScheduledCallActions: the dialog emitted a non-numeric startAt, not sending", {
				conversationId: props.conversationId,
				payload
			});
			return;
		}
		if (isRequestInFlight) {
			console.warn("ScheduledCallActions: a reschedule is already in flight for this call, ignoring the repeat submit", {
				conversationId: props.conversationId
			});
			return;
		}

		isRequestInFlight = true;
		isRescheduling.value = true;
		try {
			let rescheduledConversation: unknown;
			try {
				rescheduledConversation = await api.rescheduleScheduledCall(props.conversationId, {
					startAt: payload.startAt,
					timeZone: payload.timeZone,
					note: payload.note
				});
			} catch (error) {
				// The dialog stays OPEN so the user can adjust and retry — the server's own copy ("Pick a
				// time in the future.", "This call was just rescheduled. Please refresh and try again.") is
				// actionable, and closing would throw away the note they wrote.
				if (isUnmounted) {
					console.warn("ScheduledCallActions: reschedule failed after the call was navigated away from — not announced", {
						conversationId: props.conversationId
					});
					return;
				}
				const errorMessage = getMessageFromError(error, "Sorry, we were unable to reschedule this call. Please try again.");
				toast.error(errorMessage, { timeout: calculateTimeout(errorMessage) });
				return;
			}

			if (isUnmounted) {
				// The write succeeded and is logged, so it is not silent — it is just not announced over a
				// conversation the user is no longer looking at.
				console.warn("ScheduledCallActions: reschedule succeeded after the call was navigated away from — applied, not announced", {
					conversationId: props.conversationId
				});
				return;
			}
			isRescheduleDialogOpen.value = false;
			announceReschedule((rescheduledConversation as { rescheduleNotice?: RescheduleNotice } | undefined)?.rescheduleNotice);

			// The endpoint returns the full conversation, in the same shape as GET /conversations/:id, so
			// the parent merges what we already have rather than issuing a second request that could fail
			// and leave the card contradicting the toast.
			const rescheduled = rescheduledConversation as { id?: string } | undefined;
			if (rescheduled && typeof rescheduled === "object" && rescheduled.id) {
				emit("rescheduled", rescheduled as Record<string, any>);
			} else {
				// The write succeeded but the response is not something the page can merge. Not an ERROR
				// toast — that would contradict the success just reported — but the card is now stale, so
				// say so rather than leaving the user to discover it.
				console.warn("ScheduledCallActions: the reschedule response carried no conversation to merge", {
					conversationId: props.conversationId,
					responseType: typeof rescheduledConversation
				});
				const staleCardMessage = "The call was rescheduled, but this page couldn't refresh. Reload to see the new time.";
				toast.info(staleCardMessage, { timeout: calculateTimeout(staleCardMessage) });
			}
		} finally {
			isRequestInFlight = false;
			isRescheduling.value = false;
		}
	}

	defineExpose({ openRescheduleDialog, reschedule, isRescheduleDialogOpen, isRescheduling });
</script>
